import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import './Dashboard.css';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { ROLES } from '../config/routes';
import EnhancedNotificationButton, { useNotificationRefresh } from '../components/EnhancedNotificationButton';

const Dashboard = ({ children, page, handleCompanyId}) => {

  const navigate = useNavigate();
  const [userData, setUserData] = useState({
    "name": "",
    "email": "",
    "company": ""
  });
  // const [companies, setCompanies] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { isLoggedIn, userRole, login, logout } = useAuth();
  const { selectedCompany, companies, setCompanies, selectCompany } = useCompany();

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }

    const names = localStorage.getItem('names');
    const email = localStorage.getItem('email');

    setUserData({
        "name": names,
        "email": email,
        "company": ""
    });
    
    // In a real application, you would fetch user data from API here
    // For now, we're using the hardcoded data above
  }, [navigate]);

  const [profileImage, setProfileImage] = useState("https://via.placeholder.com/40");

  useEffect(() => {
    const fetchProfileImage = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
        }
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/pic`, {
          headers: {
            'Authorization': `Bearer ${token}`, // Make sure you have access to token
          },
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const imageUrl = URL.createObjectURL(blob);
          setProfileImage(imageUrl);
        }
      } catch (error) {
        console.error('Failed to fetch profile image:', error);
      }
    };

    fetchProfileImage();
    
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return; // Exit early if no token
        }

        if (userRole == ROLES.ADMIN) {
          return;
        }
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/companies`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setCompanies(data);
        } else {
          const errorData = await response.json();
          console.error('API error:', errorData);
          
          // Handle different error scenarios
          if (response.status === 401) {
            // Token expired or invalid
            localStorage.removeItem('token');
            navigate('/login');
          }
        }
      } catch (error) {
        console.error('Network error:', error);
        // Handle network errors or other exceptions
      }
    };
  
    fetchCompanies();
  }, [navigate]); // Add navigate to dependency array


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  
  // Updated component methods
  const handleCompanySelect = (company) => {
    // Save to localStorage
    selectCompany(company);
    setIsOpen(false);
  };

  const handleLogout = () => {
    logout();
    selectCompany(null);
    navigate('/login');
  };

  const getCurrentCompany = () => {
    return companies.find(company => company.name === selectedCompany?.name) || 
           { name: selectedCompany?.name || 'Select Company', logo: selectedCompany?.name?.[0] || 'S' };
  };

  const currentCompany = getCurrentCompany();

  return (
    <div className="dashboard-container">

      <div className="dashboard-sidebar">
        <div className="sidebar-logo">
          <img src="/logo.png" alt="Parachute" />
        </div>

        <nav className="sidebar-nav">
          <ul>
            <li 
                className={page === 'home' ? 'active' : ''} 
                onClick={() => navigate('/')}>
              <span className="nav-icon">🏠</span>
              <span>Dashboard</span>
            </li>

            { userRole == ROLES.FUND_MANAGER &&
                <>
                <li 
                    className={page === 'company' ? 'active' : ''} 
                    onClick={() => navigate('/company-profile')} >
                  <span className="nav-icon">🏢</span>
                  <span>Company Profile</span>
                </li>
                <li 
                    className={page === 'application' ? 'active' : ''} 
                    onClick={() => navigate('/applications')}>
                  <span className="nav-icon">👥</span>
                  <span>All Applicants</span>
                </li>
                <li 
                    className={page === 'listing' ? 'active' : ''} 
                    onClick={() => navigate('/listing')} >
                  <span className="nav-icon">📋</span>
                  <span>Job Listing</span>
                </li>
                <li 
                    className={page === 'posts' ? 'active' : ''} 
                    onClick={() => navigate('/posts')} >
                  <span className="nav-icon">📢</span>
                  <span>Posts</span>
                </li>
                </>
            }

            { userRole == ROLES.ADMIN &&
              <>
                <li 
                    className={page === 'listing' ? 'active' : ''} 
                    onClick={() => navigate('/admin-listing')} >
                    <span className="nav-icon">📋</span>
                    <span>Job Listing</span>
                </li>
                <li 
                    className={page === 'company' ? 'active' : ''} 
                    onClick={() => navigate('/admin-company-listing')} >
                    <span className="nav-icon">🏭</span>
                    <span>Companies</span>
                </li>
                <li 
                    className={page === 'users' ? 'active' : ''} 
                    onClick={() => navigate('/list-users')} >
                    <span className="nav-icon">🧑</span>
                    <span>Users</span>
                </li>
                <li 
                    className={page === 'posts' ? 'active' : ''} 
                    onClick={() => navigate('/posts')} >
                  <span className="nav-icon">📢</span>
                  <span>Posts</span>
                </li>
              </>
            }
          </ul>
        </nav>

        <div className="sidebar-divider">SETTINGS</div>

        <nav className="sidebar-nav">
          <ul>
            <li
                className={page === 'teams' ? 'active' : ''} 
                onClick={() => navigate('/teams')} >
              <span className="nav-icon">🆔</span>
              <span>Settings</span>
            </li>
            <li>
              <span className="nav-icon">❓</span>
              <span>Help Center</span>
            </li>
          </ul>
        </nav>

        <div className="sidebar-user" onClick={handleLogout}>
          <img 
            // src="https://via.placeholder.com/40"
            src={profileImage} 
            alt={userData.name} 
            className="user-avatar" 
          />
          <div className="user-info">
            <h4>{userData.name}</h4>
            <p>{userData.email}</p>
          </div>
        </div>
      </div>

      <div className="dashboard-main">
        <div className="dashboard-header">

        { userRole !== ROLES.ADMIN &&
        <div className="company-selector-container" ref={dropdownRef}>
                <div 
                    className={`company-selector ${isOpen ? 'open' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <div className="company-logo">
                    {currentCompany.logo}
                    </div>
                    <div className="company-info">
                    <span>Company</span>
                    <h3>
                        {currentCompany.name} 
                        <span className={`dropdown-icon ${isOpen ? 'rotated' : ''}`}>▼</span>
                    </h3>
                    </div>
                </div>

            {isOpen && (
                <div className="company-dropdown">
                {companies.length === 0 ? (
                    <div className="no-companies">No companies available</div>
                ) : (
                    companies.map((company) => (
                    <div 
                        key={company.id}
                        className={`company-option ${company.name === userData?.company ? 'selected' : ''}`}
                        onClick={() => handleCompanySelect(company)}
                    >
                        <div className="company-logo">{company.logo}</div>
                        <div className="company-name">{company.name}</div>
                        {company.name === userData?.company && (
                        <div className="selected-indicator">✓</div>
                        )}
                    </div>
                    ))
                )}
                </div>
            )}
          </div>}
          
          { userRole !== ROLES.ADMIN &&
            <div className="header-actions">
              <EnhancedNotificationButton 
                refreshInterval={30000}  // Refresh every 30 seconds
                showTooltip={true}       // Show hover tooltip
                animateOnNew={true}      // Animate when new notifications arrive
              />
              <button onClick={ () => {navigate("/create-job")} } className="post-job-btn">
                <span>+</span> Post a job
              </button>
            </div>
          }

        </div>

        {children}

      </div>
    </div>
  );
};

export default Dashboard;